import moment from 'moment-timezone'
import React, { useEffect, useState } from 'react'

import { Modal } from 'syft-acp-core/components/Modal'

import {
  CLOCK_EDIT_REASONS,
  ClockEditBooking,
  ClockEditReason,
  ClockEditReasonsByBooking,
} from './clockEditReason'
import './ClockEditReasonModal.css'

type ClockEditReasonModalProps = {
  bookings: ClockEditBooking[]
  isShown: boolean
  onClose: () => void
  onSubmit: (reasons: ClockEditReasonsByBooking) => void
}

const formatClockTime = (value: string | null | undefined, timeZone?: string) => {
  if (!value) return 'Not set'

  const time = moment(value)
  if (!time.isValid()) return value

  return timeZone ? time.tz(timeZone).format('D MMM, HH:mm') : time.format('D MMM, HH:mm')
}

export const ClockEditReasonModal = ({ bookings, isShown, onClose, onSubmit }: ClockEditReasonModalProps) => {
  const [applyToAllReason, setApplyToAllReason] = useState<ClockEditReason | ''>('')
  const [reasons, setReasons] = useState<ClockEditReasonsByBooking>({})

  useEffect(() => {
    if (isShown) {
      setApplyToAllReason('')
      setReasons({})
    }
  }, [bookings, isShown])

  const applyReasonToAll = (reason: ClockEditReason | '') => {
    setApplyToAllReason(reason)
    setReasons(reason ? Object.fromEntries(bookings.map(booking => [booking.key, reason])) : {})
  }

  const setBookingReason = (bookingKey: string, reason: ClockEditReason | '') => {
    setReasons(currentReasons => {
      if (!reason) {
        const { [bookingKey]: _removedReason, ...remainingReasons } = currentReasons

        return remainingReasons
      }

      return { ...currentReasons, [bookingKey]: reason }
    })
  }

  const canSubmit = bookings.length > 0 && bookings.every(booking => Boolean(reasons[booking.key]))

  return (
    <Modal
      className="clock-edit-reason-modal"
      header="Add a reason for clock edits"
      isShown={isShown}
      onClose={onClose}
      onConfirm={() => onSubmit(reasons)}
      confirmationText="Submit changes"
      canSubmit={canSubmit}
    >
      <p>Choose why each worker&apos;s clock time was changed. These reasons are for internal auditing.</p>

      {bookings.length > 1 && (
        <div className="form-group clock-edit-reason-modal__apply-all">
          <label htmlFor="clock-edit-reason-apply-all">Apply one reason to all workers</label>
          <select
            className="form-control"
            id="clock-edit-reason-apply-all"
            value={applyToAllReason}
            onChange={event => applyReasonToAll(event.target.value as ClockEditReason | '')}
          >
            <option value="">Select a reason</option>
            {CLOCK_EDIT_REASONS.map(reason => (
              <option key={reason.value} value={reason.value}>
                {reason.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="table-responsive">
        <table className="table clock-edit-reason-modal__table">
          <thead>
            <tr>
              <th scope="col">Worker / time change</th>
              <th scope="col">Reason</th>
            </tr>
          </thead>
          <tbody>
            {bookings.map(booking => {
              const reasonSelectId = `clock-edit-reason-${booking.key}`

              return (
                <tr key={booking.key}>
                  <th scope="row">
                    <div>{booking.workerName}</div>
                    <div className="clock-edit-reason-modal__time-change">
                      {booking.changes.map(change => (
                        <div key={change.label}>
                          <strong>{change.label}:</strong>{' '}
                          {formatClockTime(change.previousValue, booking.timeZone)} →{' '}
                          {formatClockTime(change.nextValue, booking.timeZone)}
                        </div>
                      ))}
                    </div>
                  </th>
                  <td>
                    <div className="form-group">
                      <label className="sr-only" htmlFor={reasonSelectId}>
                        Reason for {booking.workerName}
                      </label>
                      <select
                        className="form-control"
                        id={reasonSelectId}
                        value={reasons[booking.key] ?? ''}
                        onChange={event =>
                          setBookingReason(booking.key, event.target.value as ClockEditReason | '')
                        }
                      >
                        <option value="">Select a reason</option>
                        {CLOCK_EDIT_REASONS.map(reason => (
                          <option key={reason.value} value={reason.value}>
                            {reason.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </Modal>
  )
}
